import { action, computed, makeObservable, observable, runInAction } from "mobx";
import remotedev from "mobx-remotedev";
import { getLists } from "./services/getLists";
import { getLessons } from "./services/getLessons";
import { Id, LessonDI, lessonsWithReferencedObjects, ListItem, Lists, } from "./types";
import _ from "lodash";
import { deleteLesson } from "./services/deleteLesson";
import { approveLesson } from "./services/approveLesson";

interface SearchCheckboxField {
  listName: keyof Lists;
  selectedValues: Array<Id>;
  allValues: Array<Id>;
}

interface FilterTypeShow {
  type: string;
  show: boolean;
}

export interface MultipleValueFields {
  readonly [index: string]: SearchCheckboxField;
  projectTypeId: SearchCheckboxField;
  externalFactorInvolvedId: SearchCheckboxField;
  stepId: SearchCheckboxField;
  organizationalUnitId: SearchCheckboxField;
  projectNameId: SearchCheckboxField;
  lessonTags: SearchCheckboxField;
  fieldId: SearchCheckboxField;
  projectPartnerIds: SearchCheckboxField;
}

interface LessonSearch {
  resultsPerPage: number;
  pageNumber: number;
  isApproved: boolean;
  freeSearch: string;
  multipleValueFields: MultipleValueFields;
}

const initialStateSearch: LessonSearch = {
  pageNumber: 1,
  resultsPerPage: 5,
  isApproved: true,
  freeSearch: "",
  multipleValueFields: {
    projectTypeId: {
      listName: "projectTypeId",
      selectedValues: [],
      allValues: [],
    },
    externalFactorInvolvedId: {
      listName: "externalFactorInvolvedId",
      selectedValues: [],
      allValues: [],
    },
    stepId: {
      listName: "stepId",
      selectedValues: [],
      allValues: [],
    },
    organizationalUnitId: {
      listName: "organizationalUnitId",
      selectedValues: [],
      allValues: [],
    },
    projectNameId: {
      listName: "projectNameId",
      selectedValues: [],
      allValues: [],
    },
    fieldId: {
      listName: "fieldId",
      selectedValues: [],
      allValues: [],
    },
    projectPartnerIds: {
      listName: "projectPartnerIds",
      selectedValues: [],
      allValues: []
    },
    lessonTags: {
      listName: "lessonTags",
      selectedValues: [],
      allValues: []
    }
  },
};

export class LessonStore {
  search = _.cloneDeep(initialStateSearch);
  lists: Lists = {
    projectTypeId: [],
    stepId: [],
    fieldId: [],
    externalFactorInvolvedId: [],
    organizationalUnitId: [],
    projectPartnerIds: [],
    projectNameId: [],
    lessonTags: [],
    maxShow: 0,
    order: [],
    docs: []
  };
  lessons: Array<LessonDI> = [];
  lessonsMobile: Array<LessonDI> = [];
  lessonsCount: number = 0;
  unApprovedLessonsCount: number = 0;
  lessonJustAdded: boolean = false;
  lessonNotification: boolean = false;
  lessonJustDeleted: boolean = false;
  lessonJustSaved: boolean = false;

  constructor() {
    makeObservable(this, {
      search: observable,
      lessons: observable,
      lessonsMobile: observable,
      lists: observable,
      getLists: action,
      getLessons: action,
      deleteLesson: action,
      approveLesson: action,
      lessonsWithReferencedLists: computed,
      lessonsMobileWithReferencedLists: computed,
      toggleSearchBoxField: action,
      addSearchBoxFieldValue: action,
      removeSearchBoxFieldValue: action,
      setIsApproved: action,
      setFreeSearch: action,
      setFieldId: action,
      resetFields: action,
      setPageNumber: action,
      setResultsPerPage: action,
      numPages: computed,
      getSearchObject: action,
      lessonJustAdded: observable,
      setLessonJustAdded: action,
      lessonJustSaved: observable,
      setLessonJustSaved: action,
      lessonNotification: observable,
      setLessonNotification: action,
      lessonJustDeleted: observable,
      setLessonJustDeleted: action,
    });
  }

  resetFields = () => {
    this.search = initialStateSearch;
  };

  resetField = (fieldName: keyof MultipleValueFields) => {
    this.search.multipleValueFields[fieldName].selectedValues = [];
  };

  setLessonJustAdded = (lessonJustAdded: boolean) => {
    this.lessonJustAdded = lessonJustAdded;
  };

  setLessonJustSaved = (lessonJustSaved: boolean) => {
    this.lessonJustSaved = lessonJustSaved;
  };

  setLessonNotification = (lessonNotification: boolean) => {
    this.lessonNotification = lessonNotification;
  }

  setLessonJustDeleted = (lessonJustDeleted: boolean) => {
    this.lessonJustDeleted = lessonJustDeleted;
  };

  setPageNumber = (number: number) => {
    this.search.pageNumber = number;
  };
  setResultsPerPage = (number: number) => {
    this.search.resultsPerPage = number;
  };

  setFieldId = (ids: Array<Id>) => {
    this.search.multipleValueFields.fieldId.selectedValues = ids;
  };

  setSearchBoxFieldValues = (fieldName: keyof MultipleValueFields, values: Array<Id>) => {
    this.search.multipleValueFields[fieldName].selectedValues = values;
  };

  setIsApproved = (isApproved: boolean) => {
    this.search.isApproved = isApproved;
  };

  setFreeSearch = (search: string) => {
    this.search.freeSearch = search;
  };

  toggleSearchBoxField = (fieldName: keyof MultipleValueFields | string, value: Id, checked: boolean) => {
    const alreadyAdded = this.search.multipleValueFields[fieldName].selectedValues.findIndex((el: any) => el == value) > -1 ? true : false;
    if (checked) {
      if (!alreadyAdded) {
        return this.addSearchBoxFieldValue(fieldName, value);
      }
    } else {
      this.removeSearchBoxFieldValue(fieldName, value);
    }
  };

  addSearchBoxFieldValue = (fieldName: keyof MultipleValueFields, value: Id) => {
    this.search.multipleValueFields[fieldName].selectedValues = [
      ...this.search.multipleValueFields[fieldName].selectedValues,
      value,
    ];
  };

  removeSearchBoxFieldValue = (fieldName: keyof MultipleValueFields, value: Id) => {
    this.search.multipleValueFields[fieldName].selectedValues = this.search.multipleValueFields[fieldName].selectedValues.filter((id) => {
      return id != value;
    });
  };

  getListItemsFromItemsIds = (ids: Array<number | string>, list: Array<ListItem>) => {
    const items = [];
    for (let item of list) {
      if (ids.includes(item.id)) {
        items.push(item);
      }
    }
    return items;
  };

  getListItemFromItemId = (id: number | string, list: Array<ListItem>) => {
    let item = null;
    for (let i of list) {
      if (i.id == id) {
        item = i;
      }
    }
    return item;
  };

  refreshLessonsAndLists = async (isMobile: boolean) => { //This should be transparent to the user.
    await Promise.all([this.getLessons(isMobile), this.getLists()]);
  }

  approveLesson = async (id: number, isMobile: boolean) => {
    await approveLesson(id)
    await this.refreshLessonsAndLists(isMobile);
  }

  deleteLesson = async (id: Id, isMobile: boolean) => {
    await deleteLesson(id);
    await this.refreshLessonsAndLists(isMobile);
  };

  get numPages() {
    const lessons = this.lessons; //Making this reference just in order to invoke this computed, when new lessons arrive.
    let numPages;
    if (this.search.isApproved) {
      numPages = Math.ceil(this.lessonsCount / this.search.resultsPerPage);
    } else {
      numPages = Math.ceil(
        this.unApprovedLessonsCount / this.search.resultsPerPage
      );
    }
    return numPages;
  }

  /**
   * Return a lesson object with references to its various list objects, instead of references to their ids.
   */
  get lessonsWithReferencedLists(): Array<lessonsWithReferencedObjects> {
    return this.lessons.map(lesson => {
      return {
        ...lesson,
        createDate: lesson.createDate ? lesson.createDate : null,
        lastUpdate: lesson.lastUpdate ? lesson.lastUpdate : null,
        isUpdated: lesson.isEdited,
        projectPartnerIds: !lesson.projectPartnerIds
          ? null
          : this.getListItemsFromItemsIds(
            lesson.projectPartnerIds,
            this.lists.projectPartnerIds
          ),
        projectNameId: !lesson.projectNameId
          ? null
          : this.getListItemFromItemId(
            lesson.projectNameId,
            this.lists.projectNameId
          ),
        field: !lesson.fieldId
          ? null
          : this.getListItemFromItemId(lesson.fieldId, this.lists.fieldId),
        projectTypeId: !lesson.projectTypeId
          ? null
          : this.getListItemFromItemId(
            lesson.projectTypeId,
            this.lists.projectTypeId
          ),
        step: !lesson.stepId
          ? null
          : this.getListItemFromItemId(lesson.stepId, this.lists.stepId),
        organizationalUnit: !lesson.organizationalUnitId
          ? null
          : this.getListItemFromItemId(
            lesson.organizationalUnitId,
            this.lists.organizationalUnitId
          ),
        externalFactorInvolved: !lesson.externalFactorInvolvedId
          ? null
          : this.getListItemFromItemId(
            lesson.externalFactorInvolvedId,
            this.lists.externalFactorInvolvedId
          ),
      }
    })
  }

  get lessonsMobileWithReferencedLists(): Array<lessonsWithReferencedObjects> {
    return this.lessonsMobile.map((lesson) => {
      return {
        ...lesson,
        createDate: lesson.createDate ? lesson.createDate : null,
        lastUpdate: lesson.lastUpdate ? lesson.lastUpdate : null,
        isUpdated: lesson.isEdited,
        projectPartnerIds: !lesson.projectPartnerIds
          ? null
          : this.getListItemsFromItemsIds(
            lesson.projectPartnerIds,
            this.lists.projectPartnerIds
          ),
        projectNameId: !lesson.projectNameId
          ? null
          : this.getListItemFromItemId(
            lesson.projectNameId,
            this.lists.projectNameId
          ),
        field: !lesson.fieldId
          ? null
          : this.getListItemFromItemId(lesson.fieldId, this.lists.fieldId),
        projectTypeId: !lesson.projectTypeId
          ? null
          : this.getListItemFromItemId(
            lesson.projectTypeId,
            this.lists.projectTypeId
          ),
        step: !lesson.stepId
          ? null
          : this.getListItemFromItemId(lesson.stepId, this.lists.stepId),
        organizationalUnit: !lesson.organizationalUnitId
          ? null
          : this.getListItemFromItemId(
            lesson.organizationalUnitId,
            this.lists.organizationalUnitId
          ),
        externalFactorInvolved: !lesson.externalFactorInvolvedId
          ? null
          : this.getListItemFromItemId(
            lesson.externalFactorInvolvedId,
            this.lists.externalFactorInvolvedId
          ),
      };
    });
  }

  getLists = async () => {
    const lists = await getLists(); //adaptation

    let adptLists: Lists = {
      projectTypeId: [],
      stepId: [],
      fieldId: [],
      externalFactorInvolvedId: [],
      organizationalUnitId: [],
      projectPartnerIds: [],
      projectNameId: [],
      lessonTags: [],
      maxShow: 0,
      order: [],
      docs: []
    };

    adptLists.maxShow = lists.maxShow;

    for (let i = 0; i < lists.Lists.length; i++) {
      switch (lists.Lists[i].t) {
        case "ProjectTypes": {
          adptLists["projectTypeId"] = lists.Lists[i].l;
          adptLists.order.push({
            type: "projectTypeId",
            show: lists.Lists[i].show,
          });
          break;
        }
        case "ProjectSteps": {
          adptLists["stepId"] = lists.Lists[i].l;
          adptLists.order.push({ type: "stepId", show: lists.Lists[i].show });
          break;
        }
        case "Fields": {
          adptLists["fieldId"] = lists.Lists[i].l;
          adptLists.order.push({ type: "fieldId", show: lists.Lists[i].show });
          break;
        }
        case "ProjectNames": {
          adptLists["projectNameId"] = lists.Lists[i].l;
          adptLists.order.push({
            type: "projectNameId",
            show: lists.Lists[i].show,
          });
          break;
        }
        case "ExternalFactorInvolved": {
          adptLists["externalFactorInvolvedId"] = lists.Lists[i].l;
          adptLists.order.push({
            type: "externalFactorInvolvedId",
            show: lists.Lists[i].show,
          });
          break;
        }
        case "OrganizationalUnits": {
          adptLists["organizationalUnitId"] = lists.Lists[i].l;
          adptLists.order.push({
            type: "organizationalUnitId",
            show: lists.Lists[i].show,
          });
          break;
        }
        case "LessonTags": {
          adptLists["lessonTags"] = lists.Lists[i].l;
          adptLists.order.push({ type: "lessonTags", show: lists.Lists[i].show });
          break;
        }
        case "ProjectPartners": {
          adptLists["projectPartnerIds"] = lists.Lists[i].l;
          adptLists.order.push({
            type: "projectPartnerIds",
            show: lists.Lists[i].show,
          });
          break;
        }
      }
    }

    runInAction(() => {
      this.lists = adptLists;
    });
  };

  getSearchObject = () => {
    return {
      freeSearch: this.search.freeSearch,
      pageNumber: this.search.pageNumber,
      lessonTags: this.search.multipleValueFields.lessonTags.selectedValues,
      resultsPerPage: this.search.resultsPerPage,
      fieldId: this.search.multipleValueFields.fieldId.selectedValues,
      externalFactorInvolvedId: this.search.multipleValueFields.externalFactorInvolvedId.selectedValues,
      organizationalUnitId: this.search.multipleValueFields.organizationalUnitId.selectedValues,
      projectTypeId: this.search.multipleValueFields.projectTypeId.selectedValues,
      projectNameId: this.search.multipleValueFields.projectNameId.selectedValues,
      stepId: this.search.multipleValueFields.stepId.selectedValues,
      projectPartnerIds: this.search.multipleValueFields.projectPartnerIds.selectedValues,
      isApproved: this.search.isApproved,
    };
  };

  getLessons = async (isMobile: boolean) => {
    let mobileLessons: any = [];
    if (isMobile) {
      const response = await this.fetchAllMobileLessons();
      // mobileLessons = response;
      this.lessonsMobile = response;
    }
    const { lessons, lessonsCount, unApprovedLessonsCount } = await getLessons(
      this.getSearchObject()
    );
    runInAction(() => {
      this.lessons = lessons.map((lesson) => {
        return {
          ...lesson,
          id: lesson.Id, //Original type has uppercase Id..
          url: lesson.url
        };
      });
      if (!isMobile) {
        this.lessonsMobile = lessons.map((lesson) => {
          return {
            ...lesson,
            id: lesson.Id, //Original type has uppercase Id..
            url: lesson.url
          };
        });
      }
      this.unApprovedLessonsCount = unApprovedLessonsCount;
      this.lessonsCount = lessonsCount;
    });
  };

  fetchAllMobileLessons = async () => {
    let mobileLessons: any = [];
    const promises = [];

    for (let i = 0; i < this.search.pageNumber; ++i) {
      promises.push(this.getLessonsMobile(i + 1));
    }

    await Promise.all(promises).then((results: any) => {
      for (let i = 0; i < results.length; i++) {
        for (let j = 0; j < results[i].length; j++) {
          mobileLessons.push(results[i][j] as LessonDI);
        }
        //mobileLessons.push(results[i]);
      }
      // this.lessonsMobile = mobileLessons;
      // runInAction(() => {
      //   this.lessonsMobile = mobileLessons;
      // })
    });
    return mobileLessons;
  }

  getLessonsMobile = async (pageNumber: number) => {
    let searchObject = this.getSearchObject();
    searchObject.pageNumber = pageNumber;
    const { lessons, lessonsCount, unApprovedLessonsCount } = await getLessons(searchObject);
    let mobileLessons = [];
    mobileLessons.push(lessons);
    mobileLessons = lessons.map((lesson) => {
      return {
        ...lesson,
        id: lesson.Id, //Original type has uppercase Id..
        url: lesson.url
      };
    });
    this.unApprovedLessonsCount = unApprovedLessonsCount;
    this.lessonsCount = lessonsCount;
    return mobileLessons;
  }
}

const withRemoteDev = remotedev(LessonStore);
export default new withRemoteDev();
